import { unref as _unref, createVNode as _createVNode, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, toDisplayString as _toDisplayString, normalizeClass as _normalizeClass, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode } from "vue"

const _hoisted_1 = { class: "max-w-[1040px]" }
const _hoisted_2 = { class: "flex items-baseline justify-between mb-1.5" }
const _hoisted_3 = ["for"]
const _hoisted_4 = { class: "flex items-baseline justify-between mb-1.5" }
const _hoisted_5 = ["for"]
const _hoisted_6 = { class: "min-w-0" }
const _hoisted_7 = ["id"]
const _hoisted_8 = { class: "text-[13px] leading-relaxed text-gray-500" }
const _hoisted_9 = { class: "rounded bg-gray-100 px-1.5 py-0.5 font-mono text-xs text-gray-600" }

import { ulid } from 'ulid'
import { computed, inject, onMounted, reactive, ref } from 'vue'
import { useStore } from 'vuex'

import InputArea from '@/components/neue/InputArea'
import InputField from '@/components/neue/InputField'
import NeueLoading from '@/components/neue/NeueLoading'
import NeueToggle from '@/components/neue/NeueToggle'
import SettingsSection from '@/components/neue/SettingsSection.vue'
import api from '@/store/api'
import { mapRelationship } from '@/store/mappers'

const DESCRIPTION_GUIDE = 160
const MESSAGE_GUIDE = 240


export default {
  __name: 'StoreBasics',
  props: ['id'],
  setup(__props) {

const lengthGuideClass = (text, guide) => (text.length > guide ? 'text-amber-600' : 'text-gray-400')

const descriptionId = ulid()
const messageId = ulid()
const mainStoreLabelId = ulid()

const bus = inject('bus')
const store = useStore()
const loading = ref(true)
const props = __props
const storeData = computed(() => mapRelationship({ type: 'store', id: props.id }))
const isCateringStore = computed(() => storeData.value?.attributes?.platform === 'catering')
const merchantHandle = computed(
  () => store.state.loggedInUser.attributes?.handle_lower || store.state.loggedInUser.attributes?.handle
)
const merchantDomain = computed(() => `${merchantHandle.value}.bottle.com`)

const basics = reactive({
  name: '',
  description: '',
  banner_description: '',
  default_merchant_store: false
})

const load = () => {
  basics.name = storeData.value.attributes.store_name
  basics.description = storeData.value.attributes.store_description ?? ''
  basics.banner_description = storeData.value.attributes.banner_description ?? ''
  basics.default_merchant_store = storeData.value.attributes.default_merchant_store
}

onMounted(async () => {
  await api.call('GET', `stores/${props.id}?included=none`)
  loading.value = false
  load()
})

const dirty = () => bus.emit('dirty/store', { from: 'basics' })

// The badge beside the title reads the saved store, so this waits for Save like
// every other field rather than writing the moment it is flipped.
const setDefaultMerchantStore = value => {
  basics.default_merchant_store = value
  dirty()
}

bus.on('save/store', () => {
  api.call('PUT', `stores/${props.id}?included=none`, {
    store: {
      store_name: basics.name,
      store_description: basics.description,
      banner_description: basics.banner_description,
      default_merchant_store: basics.default_merchant_store
    }
  })
})

bus.on('discard/store', load)

return (_ctx, _cache) => {
  return (_openBlock(), _createBlock(_unref(NeueLoading), { loading: loading.value }, {
    default: _withCtx(() => [
      _createElementVNode("div", _hoisted_1, [
        _createVNode(SettingsSection, {
          title: "Store basics",
          description: "How this store is named and described to customers on the store selection screen, receipts, and your dashboard."
        }, {
          default: _withCtx(() => [
            _createVNode(_unref(InputField), {
              modelValue: basics.name,
              "onUpdate:modelValue": _cache[0] || (_cache[0] = $event => ((basics.name) = $event)),
              name: "Store name",
              onInput: dirty
            }, null, 8, ["modelValue"]),
            _createElementVNode("div", null, [
              _createElementVNode("div", _hoisted_2, [
                _createElementVNode("label", {
                  for: _unref(descriptionId),
                  class: "text-sm font-medium text-gray-700"
                }, [...(_cache[3] || (_cache[3] = [
                  _createTextVNode(" Description ", -1),
                  _createElementVNode("span", { class: "font-normal text-gray-400" }, "· optional", -1)
                ]))], 8, _hoisted_3),
                _createElementVNode("span", {
                  class: _normalizeClass(["text-xs tabular-nums", lengthGuideClass(basics.description, DESCRIPTION_GUIDE)])
                }, _toDisplayString(basics.description.length) + " / " + _toDisplayString(DESCRIPTION_GUIDE), 3)
              ]),
              _createVNode(_unref(InputArea), {
                id: _unref(descriptionId),
                modelValue: basics.description,
                "onUpdate:modelValue": _cache[1] || (_cache[1] = $event => ((basics.description) = $event)),
                rows: 3,
                placeholder: "e.g. Fresh breakfast and brunch, made to order.",
                description: "Shown under the store name on the store selection screen.",
                onInput: dirty
              }, null, 8, ["id", "modelValue"])
            ])
          ]),
          _: 1
        }),
        _createVNode(SettingsSection, {
          title: "Store page message",
          description: "A short note shown below your banner image. Good for ordering windows, pickup and drop-off info, promos, or a welcome."
        }, {
          default: _withCtx(() => [
            _createElementVNode("div", null, [
              _createElementVNode("div", _hoisted_4, [
                _createElementVNode("label", {
                  for: _unref(messageId),
                  class: "text-sm font-medium text-gray-700"
                }, [...(_cache[4] || (_cache[4] = [
                  _createTextVNode(" Message ", -1),
                  _createElementVNode("span", { class: "font-normal text-gray-400" }, "· optional", -1)
                ]))], 8, _hoisted_5),
                _createElementVNode("span", {
                  class: _normalizeClass(["text-xs tabular-nums", lengthGuideClass(basics.banner_description, MESSAGE_GUIDE)])
                }, _toDisplayString(basics.banner_description.length) + " / " + _toDisplayString(MESSAGE_GUIDE), 3)
              ]),
              _createVNode(_unref(InputArea), {
                id: _unref(messageId),
                modelValue: basics.banner_description,
                "onUpdate:modelValue": _cache[2] || (_cache[2] = $event => ((basics.banner_description) = $event)),
                rows: 3,
                placeholder: "e.g. Order by 5pm for next-day delivery. Use code WELCOME10 at checkout.",
                onInput: dirty
              }, null, 8, ["id", "modelValue"])
            ])
          ]),
          _: 1
        }),
        (!isCateringStore.value)
          ? (_openBlock(), _createBlock(SettingsSection, {
              key: 0,
              title: "Default store",
              description: "Choose which store customers land on first.",
              flush: ""
            }, {
              default: _withCtx(() => [
                _createElementVNode("div", _hoisted_6, [
                  _createElementVNode("p", {
                    id: _unref(mainStoreLabelId),
                    class: "text-sm font-semibold text-gray-700 mb-1"
                  }, "Set as main store", 8, _hoisted_7),
                  _createElementVNode("p", _hoisted_8, [
                    _cache[5] || (_cache[5] = _createTextVNode(" Customers visiting ", -1)),
                    _createElementVNode("code", _hoisted_9, _toDisplayString(merchantDomain.value), 1),
                    _cache[6] || (_cache[6] = _createTextVNode(" land here. Other stores are listed at the bottom of the store menu. ", -1))
                  ])
                ]),
                _createVNode(_unref(NeueToggle), {
                  "model-value": basics.default_merchant_store,
                  "aria-labelledby": _unref(mainStoreLabelId),
                  onChange: setDefaultMerchantStore
                }, null, 8, ["model-value", "aria-labelledby"])
              ]),
              _: 1
            }))
          : _createCommentVNode("", true)
      ])
    ]),
    _: 1
  }, 8, ["loading"]))
}
}

}